import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BaseTable,
  BaseTableBody,
  BaseTableCell,
  BaseTableContainer,
  BaseTableEmpty,
  BaseTableHead,
  BaseTableHeader,
  BaseTableRow,
} from "../base/BaseTable";
import type {
  BaseAppearance,
  BaseTableColumnOrderChangeDetail,
  BaseTableColumnSizeChangeDetail,
  BaseTableColumnSizes,
  BaseTableSortDirection,
} from "../base/types";
import { cn, hasRenderableNode } from "../base/utils";
import { renderWorkbenchIcon } from "./workbenchIconUtils";

export interface WorkbenchTableViewColumn {
  key: string;
  label?: ReactNode;
  helperText?: ReactNode;
  renderType?: string;
  type?: string;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  trailing?: ReactNode;
  wrap?: boolean;
  align?: "left" | "center" | "right";
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  sortable?: boolean;
  sortDirection?: BaseTableSortDirection;
  sortLabel?: string;
  draggable?: boolean;
  dragLabel?: string;
  resizable?: boolean;
  resizeLabel?: string;
  compactHeaderClassName?: string;
  compactCellClassName?: string;
  headerClassName?: string;
  cellClassName?: string;
  headerStyle?: CSSProperties;
  cellStyle?: CSSProperties;
  [key: string]: any;
}

export interface WorkbenchTableViewClassNames {
  root?: string;
  scrollArea?: string;
  table?: string;
  header?: string;
  headerRow?: string;
  headerCell?: string;
  body?: string;
  row?: string;
  cell?: string;
  footer?: string;
  helper?: string;
}

export interface WorkbenchTableViewRenderContext<Row = any> {
  row: Row;
  rowIndex: number;
  column: WorkbenchTableViewColumn;
  columnIndex: number;
  compact: boolean;
  surface: "list" | string;
}

export interface WorkbenchTableViewHeaderContext<Row = any> {
  column: WorkbenchTableViewColumn;
  columnIndex: number;
  columns: WorkbenchTableViewColumn[];
  rows: Row[];
  columnType?: string;
}

export interface WorkbenchTableScrollRestoration {
  key: string;
  read(): { x: number; y: number; rowKey?: string; rowOffset?: number } | undefined;
  write(position: { x: number; y: number; rowKey?: string; rowOffset?: number }): void;
}

export interface WorkbenchTableViewProps<Row = any> {
  scrollRestoration?: WorkbenchTableScrollRestoration;
  rows?: Row[];
  columns?: WorkbenchTableViewColumn[];
  onRowClick?: (row: Row, rowIndex: number) => void;
  selectedRowKey?: string;
  selectedLinkKey?: string;
  activePrimaryColumn?: WorkbenchTableViewColumn | null;
  activeTablePrimaryColumn?: WorkbenchTableViewColumn | null;
  renderedRowCount?: number;
  totalRowCount?: number;
  tableMinWidth?: number;
  minWidth?: number;
  bodyMaxHeight?: number | string;
  stickyFirstColumn?: boolean;
  appearance?: BaseAppearance;
  headerPresentation?: "classic-data-grid";
  fillRemainingWidthColumnKey?: string;
  columnOrder?: string[];
  columnSizes?: BaseTableColumnSizes;
  columnDragEnabled?: boolean;
  columnResizeEnabled?: boolean;
  className?: string;
  tableClassName?: string;
  rowClassName?: string | ((row: Row, rowIndex: number) => string | undefined);
  classNames?: WorkbenchTableViewClassNames;
  style?: CSSProperties;
  renderCell?: (
    column: WorkbenchTableViewColumn,
    row: Row,
    context: WorkbenchTableViewRenderContext<Row>,
  ) => ReactNode;
  renderFieldValue?: (
    column: WorkbenchTableViewColumn,
    row: Row,
    context: WorkbenchTableViewRenderContext<Row>,
  ) => ReactNode;
  renderColumnHeader?: (
    column: WorkbenchTableViewColumn,
    context: WorkbenchTableViewHeaderContext<Row>,
  ) => ReactNode;
  onColumnOrderChange?: (
    nextOrder: string[],
    detail: BaseTableColumnOrderChangeDetail,
  ) => void;
  onColumnSizeChange?: (
    columnId: string,
    width: number,
    detail: BaseTableColumnSizeChangeDetail,
  ) => void;
  onSort?: (columnKey: string, column: WorkbenchTableViewColumn) => void;
  formatLabel?: (
    value: ReactNode,
    context?: { column?: WorkbenchTableViewColumn },
  ) => ReactNode;
  getColumnDataType?: (
    columnKey: string,
    rows: Row[],
    column?: WorkbenchTableViewColumn,
  ) => string | undefined;
  getColumnIcon?: (
    columnType?: string,
    column?: WorkbenchTableViewColumn,
  ) => ReactNode | ComponentType<{ className?: string }> | null | undefined;
  getColumnTypeIcon?: (
    columnType?: string,
    column?: WorkbenchTableViewColumn,
  ) => ReactNode | ComponentType<{ className?: string }> | null | undefined;
  getRowKey?: (row: Row, rowIndex: number) => string | number;
  getRowSelectionKey?: (
    row: Row,
    rowIndex: number,
  ) => string | number | undefined;
  isActionColumn?: (column: WorkbenchTableViewColumn) => boolean;
  isActionField?: (column: WorkbenchTableViewColumn) => boolean;
  isRowDisabled?: (row: Row, rowIndex: number) => boolean;
  isWorkbenchPlaceholderRow?: (row: Row, rowIndex?: number) => boolean;
  emptyState?: ReactNode;
  hiddenRowsMessage?:
    | ReactNode
    | ((
        hiddenRowCount: number,
        renderedRowCount: number,
        totalRowCount: number,
      ) => ReactNode);
  scrollFooter?: ReactNode;
}

function toCssSize(value: number | string | undefined) {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

const DEFAULT_WORKBENCH_COLUMN_WIDTH = 160;
const DEFAULT_WORKBENCH_ICON_COLUMN_WIDTH = 56;
const DEFAULT_WORKBENCH_ACTION_COLUMN_WIDTH = 128;

function numericCssSize(value: number | string | undefined) {
  if (typeof value === "number" && Number.isFinite(value) && value > 0)
    return value;
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
  return match ? Number(match[1]) : undefined;
}

function defaultWorkbenchColumnWidth(
  column: WorkbenchTableViewColumn,
  action: boolean,
) {
  if (column.renderType === "icon") return DEFAULT_WORKBENCH_ICON_COLUMN_WIDTH;
  if (action) return DEFAULT_WORKBENCH_ACTION_COLUMN_WIDTH;
  return DEFAULT_WORKBENCH_COLUMN_WIDTH;
}

function resolvedWorkbenchColumnWidth(
  column: WorkbenchTableViewColumn,
  controlledWidth: number | undefined,
  action: boolean,
) {
  return (
    controlledWidth ??
    numericCssSize(column.width) ??
    numericCssSize(column.minWidth) ??
    defaultWorkbenchColumnWidth(column, action)
  );
}

function defaultFormatLabel(value: ReactNode) {
  if (value === undefined || value === null || value === false) return null;
  return value;
}

function toTitle(value: ReactNode) {
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  return undefined;
}

function defaultGetRowKey(row: any, rowIndex: number) {
  return row?.id ?? row?.key ?? row?.linkKey ?? rowIndex;
}

function defaultGetRowSelectionKey(row: any) {
  return row?.linkKey ?? row?.id ?? row?.key;
}

function getOrderedColumns(
  columns: WorkbenchTableViewColumn[],
  columnOrder?: string[],
) {
  if (!columnOrder?.length) return columns;

  const columnMap = new Map(columns.map((column) => [column.key, column]));
  const orderedColumns = columnOrder
    .map((columnKey) => columnMap.get(columnKey))
    .filter(Boolean) as WorkbenchTableViewColumn[];
  const orderedKeySet = new Set(orderedColumns.map((column) => column.key));
  const remainingColumns = columns.filter(
    (column) => !orderedKeySet.has(column.key),
  );

  return [...orderedColumns, ...remainingColumns];
}

function normalizeColumnOrder(columnKeys: string[], columnOrder?: string[]) {
  const validColumnKeys = new Set(columnKeys);
  const orderedKeys = (columnOrder || []).filter((columnKey) =>
    validColumnKeys.has(columnKey),
  );
  const orderedKeySet = new Set(orderedKeys);
  const remainingKeys = columnKeys.filter(
    (columnKey) => !orderedKeySet.has(columnKey),
  );

  return [...orderedKeys, ...remainingKeys];
}

function WorkbenchTableHeaderHelper({
  helperText,
  className,
}: {
  helperText?: ReactNode;
  className?: string;
}) {
  if (!hasRenderableNode(helperText)) return null;

  return (
    <span className={cn("group relative inline-flex items-center", className)}>
      <button
        type="button"
        title={toTitle(helperText)}
        aria-label={toTitle(helperText) || "Field help"}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border/60 bg-background text-[10px] font-semibold normal-case tracking-normal text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-primary focus-visible:border-primary/40 focus-visible:text-primary focus-visible:outline-none"
        onClick={(event) => event.stopPropagation()}
      >
        ?
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 hidden w-72 -translate-x-1/2 whitespace-pre-line rounded-lg border border-border/60 bg-card px-3 py-2 text-[11px] font-normal normal-case leading-5 tracking-normal text-foreground shadow-sm backdrop-blur-sm group-hover:block group-focus-within:block"
      >
        {helperText}
      </span>
    </span>
  );
}

export function WorkbenchTableView<Row = any>({
  rows = [],
  columns = [],
  onRowClick,
  selectedRowKey,
  selectedLinkKey,
  activePrimaryColumn,
  activeTablePrimaryColumn,
  renderedRowCount,
  totalRowCount,
  tableMinWidth,
  minWidth,
  bodyMaxHeight = "min(58vh, 38rem)",
  stickyFirstColumn = true,
  appearance,
  headerPresentation,
  fillRemainingWidthColumnKey,
  columnOrder,
  columnSizes,
  columnDragEnabled,
  columnResizeEnabled,
  className,
  tableClassName,
  rowClassName,
  classNames,
  style,
  renderCell,
  renderFieldValue,
  renderColumnHeader,
  onColumnOrderChange,
  onColumnSizeChange,
  onSort,
  formatLabel = defaultFormatLabel,
  getColumnDataType,
  getColumnIcon,
  getColumnTypeIcon,
  getRowKey = defaultGetRowKey,
  getRowSelectionKey = defaultGetRowSelectionKey,
  isActionColumn,
  isActionField,
  isRowDisabled,
  isWorkbenchPlaceholderRow,
  emptyState = "-",
  hiddenRowsMessage,
  scrollFooter,
  scrollRestoration,
}: WorkbenchTableViewProps<Row>) {
  const columnKeySignature = columns.map((column) => column.key).join("\u0001");
  const defaultColumnOrder = useMemo(
    () => (columnKeySignature ? columnKeySignature.split("\u0001") : []),
    [columnKeySignature],
  );
  const [internalColumnOrder, setInternalColumnOrder] =
    useState(defaultColumnOrder);
  const [internalColumnSizes, setInternalColumnSizes] =
    useState<BaseTableColumnSizes>({});
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const rowElements = useRef(new Map<string, HTMLTableRowElement>());
  const scrollAdapter = useRef(scrollRestoration);
  scrollAdapter.current = scrollRestoration;
  const restoredScrollKey = useRef<string | undefined>(undefined);
  const saveScroll = useCallback(() => {
    const viewport = scrollAreaRef.current;
    const adapter = scrollAdapter.current;
    if (!viewport || !adapter || restoredScrollKey.current !== adapter.key) return;
    const top = viewport.getBoundingClientRect().top;
    const anchor = Array.from(rowElements.current.entries()).find(([, element]) => element.getBoundingClientRect().bottom > top);
    adapter.write({ x: viewport.scrollLeft, y: viewport.scrollTop,
      ...(anchor ? { rowKey: anchor[0], rowOffset: anchor[1].getBoundingClientRect().top - top } : {}) });
  }, []);
  useEffect(() => {
    const viewport = scrollAreaRef.current;
    const adapter = scrollAdapter.current;
    if (!viewport || !adapter || restoredScrollKey.current === adapter.key || rows.length === 0) return;
    const position = adapter.read();
    const restore = () => {
      const anchor = position?.rowKey ? rowElements.current.get(position.rowKey) : undefined;
      const y = anchor && typeof position?.rowOffset === 'number'
        ? viewport.scrollTop + anchor.getBoundingClientRect().top - viewport.getBoundingClientRect().top - position.rowOffset
        : position?.y ?? 0;
      viewport.scrollTo({ left: position?.x ?? 0, top: Math.max(0, y), behavior: 'auto' });
      restoredScrollKey.current = adapter.key;
    };
    const ownerWindow = viewport.ownerDocument.defaultView;
    if (!ownerWindow) { restore(); return; }
    const frame = ownerWindow.requestAnimationFrame(restore);
    return () => ownerWindow.cancelAnimationFrame(frame);
  }, [rows, scrollRestoration?.key]);
  useEffect(() => {
    const ownerWindow = scrollAreaRef.current?.ownerDocument.defaultView;
    ownerWindow?.addEventListener('pagehide', saveScroll);
    return () => { ownerWindow?.removeEventListener('pagehide', saveScroll); saveScroll(); };
  }, [saveScroll]);
  const [tableViewportWidth, setTableViewportWidth] = useState<number>();

  useEffect(() => {
    setInternalColumnOrder((current) =>
      normalizeColumnOrder(defaultColumnOrder, current),
    );
  }, [defaultColumnOrder]);

  const resolvedColumnOrder = columnOrder
    ? normalizeColumnOrder(defaultColumnOrder, columnOrder)
    : internalColumnOrder;
  const resolvedColumnSizes = columnSizes ?? internalColumnSizes;
  const handleColumnOrderChange = useCallback(
    (nextOrder: string[], detail: BaseTableColumnOrderChangeDetail) => {
      const normalizedOrder = normalizeColumnOrder(
        defaultColumnOrder,
        nextOrder,
      );
      if (!columnOrder) {
        setInternalColumnOrder(normalizedOrder);
      }
      onColumnOrderChange?.(normalizedOrder, detail);
    },
    [columnOrder, defaultColumnOrder, onColumnOrderChange],
  );
  const handleColumnSizeChange = useCallback(
    (
      columnId: string,
      width: number,
      detail: BaseTableColumnSizeChangeDetail,
    ) => {
      if (!columnSizes) {
        setInternalColumnSizes((current) => ({
          ...current,
          [columnId]: width,
        }));
      }
      onColumnSizeChange?.(columnId, width, detail);
    },
    [columnSizes, onColumnSizeChange],
  );
  const resolvedSelectedKey = selectedRowKey ?? selectedLinkKey;
  const resolvedActivePrimaryColumn =
    activePrimaryColumn ?? activeTablePrimaryColumn;
  const sourceOrderedColumns = getOrderedColumns(columns, resolvedColumnOrder);
  const fixedTableWidth = fillRemainingWidthColumnKey
    ? sourceOrderedColumns.reduce((total, column) => {
        if (column.key === fillRemainingWidthColumnKey) return total;
        return (
          total +
          resolvedWorkbenchColumnWidth(
            column,
            resolvedColumnSizes[column.key],
            isActionColumn?.(column) ?? isActionField?.(column) ?? false,
          )
        );
      }, 0)
    : undefined;
  const remainingTableWidth =
    fillRemainingWidthColumnKey &&
    fixedTableWidth !== undefined &&
    tableViewportWidth !== undefined
      ? Math.max(0, Math.round(tableViewportWidth - fixedTableWidth))
      : undefined;
  const orderedColumns = sourceOrderedColumns.flatMap((column) => {
    if (column.key !== fillRemainingWidthColumnKey) return [column];
    if (remainingTableWidth === 0) return [];
    if (remainingTableWidth === undefined) return [column];
    return [
      {
        ...column,
        width: remainingTableWidth,
        minWidth: remainingTableWidth,
        maxWidth: remainingTableWidth,
      },
    ];
  });
  const hiddenRowCount = Math.max(
    0,
    Number(totalRowCount || rows.length) -
      Number(renderedRowCount || rows.length),
  );
  const renderValue =
    renderCell ??
    renderFieldValue ??
    ((column: WorkbenchTableViewColumn, row: Row) =>
      (row as any)?.[column.key]);
  const isAction = isActionColumn ?? isActionField ?? (() => false);
  const isDisabled =
    isRowDisabled ?? isWorkbenchPlaceholderRow ?? (() => false);
  const resolveColumnIcon = getColumnIcon ?? getColumnTypeIcon;
  const resolvedTableWidth =
    fixedTableWidth !== undefined && remainingTableWidth !== undefined
      ? fixedTableWidth + remainingTableWidth
      : undefined;
  const resolvedMinWidth =
    resolvedTableWidth ??
    tableMinWidth ??
    minWidth ??
    Math.max(
      760,
      orderedColumns.reduce(
        (total, column) =>
          total +
          resolvedWorkbenchColumnWidth(
            column,
            resolvedColumnSizes[column.key],
            isAction(column),
          ),
        0,
      ),
    );

  useEffect(() => {
    if (!fillRemainingWidthColumnKey) {
      setTableViewportWidth(undefined);
      return;
    }
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;
    const ownerWindow = scrollArea.ownerDocument.defaultView;
    const updateWidth = () => {
      const nextWidth = Math.round(scrollArea.getBoundingClientRect().width);
      if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;
      setTableViewportWidth((current) =>
        current === nextWidth ? current : nextWidth,
      );
    };
    updateWidth();
    const Observer = ownerWindow?.ResizeObserver;
    const observer = Observer ? new Observer(updateWidth) : undefined;
    observer?.observe(scrollArea);
    ownerWindow?.addEventListener("resize", updateWidth);
    return () => {
      observer?.disconnect();
      ownerWindow?.removeEventListener("resize", updateWidth);
    };
  }, [fillRemainingWidthColumnKey]);

  const footer =
    hiddenRowCount > 0 && hiddenRowsMessage
      ? typeof hiddenRowsMessage === "function"
        ? hiddenRowsMessage(
            hiddenRowCount,
            renderedRowCount || rows.length,
            totalRowCount || rows.length,
          )
        : hiddenRowsMessage
      : null;

  return (
    <BaseTableContainer
      appearance={appearance}
      className={cn(
        "w-full overflow-hidden rounded-lg border border-border/40 bg-muted/15 dark:border-border/35 dark:bg-muted/10",
        classNames?.root,
        className,
      )}
      style={style}
    >
      <div
        ref={scrollAreaRef}
        onScroll={saveScroll}
        className={cn("w-full overflow-auto", classNames?.scrollArea)}
        style={{ maxHeight: toCssSize(bodyMaxHeight) }}
      >
        <BaseTable
          appearance={appearance}
          columnOrder={resolvedColumnOrder}
          columnSizes={resolvedColumnSizes}
          columnDragEnabled={columnDragEnabled}
          columnResizeEnabled={columnResizeEnabled}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnSizeChange={handleColumnSizeChange}
          className={cn(
            "w-full table-auto text-left text-xs",
            classNames?.table,
            tableClassName,
          )}
          style={{
            width: resolvedTableWidth,
            minWidth: resolvedMinWidth,
          }}
        >
          <BaseTableHeader
            appearance={appearance}
            className={cn(
              "text-[10px] font-mono uppercase tracking-wide text-muted-foreground",
              classNames?.header,
            )}
          >
            <BaseTableRow
              className={cn("hover:bg-transparent", classNames?.headerRow)}
            >
              {orderedColumns.map((column, columnIndex) => {
                const defaultColumnWidth = defaultWorkbenchColumnWidth(
                  column,
                  isAction(column),
                );
                const columnType =
                  column.renderType ||
                  column.type ||
                  getColumnDataType?.(column.key, rows, column);
                const columnIcon = renderWorkbenchIcon(
                  resolveColumnIcon?.(columnType, column),
                  "h-3.5 w-3.5 shrink-0 text-primary",
                );
                const previousColumn = orderedColumns[columnIndex - 1];
                const followsRecordIcon =
                  previousColumn?.renderType === "icon" &&
                  column.key === resolvedActivePrimaryColumn?.key;
                const headerContent = renderColumnHeader
                  ? renderColumnHeader(column, {
                      column,
                      columnIndex,
                      columns: orderedColumns,
                      rows,
                      columnType,
                    })
                  : formatLabel(column.label, { column });
                const headerTrailing =
                  !renderColumnHeader &&
                  (hasRenderableNode(column.trailing) ||
                    hasRenderableNode(column.helperText)) ? (
                    <span className="inline-flex items-center gap-1.5">
                      {hasRenderableNode(column.trailing)
                        ? formatLabel(column.trailing, { column })
                        : null}
                      <WorkbenchTableHeaderHelper
                        helperText={formatLabel(column.helperText, { column })}
                        className={classNames?.helper}
                      />
                    </span>
                  ) : undefined;
                const sortable = Boolean(column.sortable);
                const handleSort =
                  sortable && onSort
                    ? () => onSort(column.key, column)
                    : undefined;

                return (
                  <BaseTableHead
                    appearance={appearance}
                    presentation={headerPresentation}
                    key={column.key}
                    columnId={column.key}
                    align={column.align}
                    icon={renderColumnHeader ? undefined : columnIcon}
                    label={headerContent}
                    description={
                      renderColumnHeader
                        ? undefined
                        : formatLabel(column.description, { column })
                    }
                    meta={
                      renderColumnHeader
                        ? undefined
                        : formatLabel(column.meta, { column })
                    }
                    actions={column.actions}
                    trailing={headerTrailing}
                    sortable={sortable}
                    sortDirection={column.sortDirection}
                    sortLabel={column.sortLabel}
                    onSort={handleSort}
                    draggable={column.draggable}
                    dragLabel={column.dragLabel}
                    resizable={column.resizable}
                    resizeLabel={column.resizeLabel}
                    width={column.width ?? defaultColumnWidth}
                    minWidth={
                      typeof column.minWidth === "number"
                        ? column.minWidth
                        : undefined
                    }
                    maxWidth={
                      typeof column.maxWidth === "number"
                        ? column.maxWidth
                        : undefined
                    }
                    className={cn(
                      "sticky top-0 z-20 h-auto whitespace-nowrap bg-card/95 px-4 py-3 font-medium backdrop-blur-sm supports-[backdrop-filter]:bg-card/90",
                      stickyFirstColumn &&
                        columnIndex === 0 &&
                        "md:left-0 md:z-30 md:shadow-[1px_0_0_0_hsl(var(--border)/0.35)]",
                      column.compactHeaderClassName,
                      column.renderType === "icon" && "pl-3 pr-1",
                      followsRecordIcon && "pl-2",
                      classNames?.headerCell,
                      column.headerClassName,
                    )}
                    style={{
                      minWidth:
                        typeof column.minWidth === "string"
                          ? column.minWidth
                          : undefined,
                      maxWidth:
                        typeof column.maxWidth === "string"
                          ? column.maxWidth
                          : undefined,
                      ...column.headerStyle,
                    }}
                  />
                );
              })}
            </BaseTableRow>
          </BaseTableHeader>
          <BaseTableBody
            appearance={appearance}
            className={cn(
              "divide-y divide-border/35 dark:divide-border/30",
              classNames?.body,
            )}
          >
            {rows.length ? (
              rows.map((row, rowIndex) => {
                const key = getRowKey(row, rowIndex);
                const disabled = isDisabled(row, rowIndex);
                const selectionKey = getRowSelectionKey(row, rowIndex);
                const selected = Boolean(
                  resolvedSelectedKey &&
                    String(selectionKey) === String(resolvedSelectedKey),
                );
                const resolvedRowClassName =
                  typeof rowClassName === "function"
                    ? rowClassName(row, rowIndex)
                    : rowClassName;

                return (
                  <BaseTableRow
                    appearance={appearance}
                    key={key}
                    ref={(element) => { if (element) rowElements.current.set(String(key), element); else rowElements.current.delete(String(key)); }}
                    interactive={Boolean(onRowClick) && !disabled}
                    selected={selected}
                    disabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        onRowClick?.(row, rowIndex);
                      }
                    }}
                    className={cn(
                      onRowClick && !disabled
                        ? "cursor-pointer"
                        : "cursor-default",
                      selected
                        ? "bg-primary/10 hover:bg-primary/15"
                        : disabled
                          ? ""
                          : "hover:bg-muted/25 dark:hover:bg-muted/15",
                      classNames?.row,
                      resolvedRowClassName,
                    )}
                  >
                    {orderedColumns.map((column, columnIndex) => {
                      const shouldWrap =
                        isAction(column) || column.wrap === true;
                      const previousColumn = orderedColumns[columnIndex - 1];
                      const followsRecordIcon =
                        previousColumn?.renderType === "icon" &&
                        column.key === resolvedActivePrimaryColumn?.key;

                      return (
                        <BaseTableCell
                          appearance={appearance}
                          key={`${key}-${column.key}`}
                          align={column.align}
                          className={cn(
                            shouldWrap
                              ? "align-top whitespace-normal"
                              : "whitespace-nowrap",
                            "px-4 py-3 text-muted-foreground",
                            !shouldWrap && "overflow-hidden text-ellipsis",
                            stickyFirstColumn &&
                              columnIndex === 0 &&
                              "md:sticky md:left-0 md:z-10 md:bg-card/95 md:text-foreground md:shadow-[1px_0_0_0_hsl(var(--border)/0.3)]",
                            column.compactCellClassName,
                            column.renderType === "icon" && "pl-3 pr-1",
                            followsRecordIcon && "pl-2",
                            classNames?.cell,
                            column.cellClassName,
                          )}
                          style={column.cellStyle}
                        >
                          {renderValue(column, row, {
                            row,
                            rowIndex,
                            column,
                            columnIndex,
                            compact: true,
                            surface: "list",
                          })}
                        </BaseTableCell>
                      );
                    })}
                  </BaseTableRow>
                );
              })
            ) : (
              <BaseTableEmpty
                appearance={appearance}
                colSpan={Math.max(orderedColumns.length, 1)}
                classNames={{
                  cell: "px-4 py-10 text-center text-sm text-muted-foreground",
                }}
              >
                {emptyState}
              </BaseTableEmpty>
            )}
          </BaseTableBody>
        </BaseTable>
        {hasRenderableNode(scrollFooter) ? (
          <div style={{ minWidth: resolvedMinWidth }}>{scrollFooter}</div>
        ) : null}
      </div>
      {footer ? (
        <div
          className={cn(
            "border-t border-border/40 px-4 py-2 text-[11px] text-muted-foreground dark:border-border/30",
            classNames?.footer,
          )}
        >
          {footer}
        </div>
      ) : null}
    </BaseTableContainer>
  );
}
